(() => {
  if (window.__EmbryniumBundleBuilderLoaded) return;
  window.__EmbryniumBundleBuilderLoaded = true;

  const ACTIVE = 'embundle-active';
  const FILLED = 'embundle-filled';
  const LIT = 'embundle-lit';
  const UNAVAILABLE = 'embundle-unavailable';
  const CARD_ADDED = 'embundle-card-added';
  const STORAGE_KEY = 'embrynium:bundle-draft:v2';

  const normalize = (value) => String(value || '')
    .toLowerCase()
    .replace(/grey/g, 'gray')
    .replace(/colour/g, 'color')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const money = (cents) => {
    const n = Number(cents || 0) / 100;
    return `$${n.toFixed(n % 1 === 0 ? 0 : 2)}`;
  };

  const cartUrl = () => {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}cart.js`.replace(/\/+/g, '/').replace('https:/', 'https://');
  };

  const readCart = async () => {
    // Use Shopify's cart endpoint directly here. This keeps hydration independent
    // from the cart drawer/service lifecycle and works in theme-editor previews.
    const response = await fetch(cartUrl(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Unable to read Shopify cart.');
    return response.json();
  };

  class EmbryniumBundleBuilder {
    constructor(root) {
      this.root = root;
      this.cards = [...root.querySelectorAll('[data-card]')];
      this.slots = [...root.querySelectorAll('[data-slot]')];
      this.checkoutButtons = [...root.querySelectorAll('[data-checkout]')];
      this.selected = [];
      this.otherEligibleQuantity = 0;
      this.syncing = false;
      this.cartSyncTimer = null;
      this.bundleState = null;
      this.variantIndex = new Map();
      this.productIndex = new Map();
      this.abortController = new AbortController();
    }

    init() {
      if (this.root.dataset.embundleReady === 'true') return;
      this.root.dataset.embundleReady = 'true';

      this.initBundleState();
      this.indexCards();
      this.initializeCards();
      this.bindEvents();
      this.render();
      this.syncFromCart();
    }

    initBundleState() {
      const configElement = this.root.querySelector('script[type="application/json"][id^="EmbryniumBundleConfig-"]');
      let config = {};
      try {
        config = configElement ? JSON.parse(configElement.textContent || '{}') : {};
      } catch (error) {
        console.warn('Embrynium bundle config could not be parsed.', error);
      }

      if (window.EmbryniumBundleState?.BundleState) {
        this.bundleState = new window.EmbryniumBundleState.BundleState(config);
      }
    }

    optionElements(card) {
      const select = card.querySelector('[data-select]');
      return select ? [...select.options] : [];
    }

    optionData(option, index) {
      return option?.getAttribute(`data-option${index}`) || '';
    }

    indexCards() {
      this.variantIndex.clear();
      this.productIndex.clear();

      this.cards.forEach((card) => {
        const productId = String(card.dataset.productId || '');
        if (productId) this.productIndex.set(productId, card);

        this.optionElements(card).forEach((option) => {
          this.variantIndex.set(String(option.value), { card, option });
        });
      });
    }

    initializeCards() {
      this.cards.forEach((card) => {
        const options = this.optionElements(card);
        const first = options[0];
        if (!first) return;

        card.dataset.currentView = card.dataset.defaultView || 'front';
        card.querySelectorAll('[data-option-group]').forEach((group) => {
          const index = Number(group.dataset.index || 0);
          const initialValue = this.optionData(first, index);
          group.querySelectorAll('[data-option-index]').forEach((button) => {
            const active = button.dataset.value === initialValue;
            button.classList.toggle(ACTIVE, active);
            button.setAttribute('aria-pressed', String(active));
          });
        });

        this.setViewButtons(card, card.dataset.currentView);
        this.updateCard(card);
      });
    }

    currentChoices(card) {
      const first = this.optionElements(card)[0];
      const choices = [];

      card.querySelectorAll('[data-option-group]').forEach((group) => {
        const index = Number(group.dataset.index || 0);
        const active = group.querySelector(`.${ACTIVE}[data-option-index]`);
        choices[index] = active?.dataset.value || this.optionData(first, index);
      });

      return choices;
    }

    variantMatches(option, choices, ignoreIndex = -1) {
      return choices.every((choice, index) => {
        if (index === ignoreIndex || !choice) return true;
        return normalize(this.optionData(option, index)) === normalize(choice);
      });
    }

    matchVariant(card, choices = this.currentChoices(card), changedIndex = -1) {
      const options = this.optionElements(card);
      if (!options.length) return null;

      const exact = options.find((option) => this.variantMatches(option, choices));
      if (exact) return exact;

      // Preserve the value the shopper most recently chose and select the closest
      // available Shopify variant for the remaining dimensions.
      if (changedIndex >= 0 && choices[changedIndex]) {
        const requested = normalize(choices[changedIndex]);
        const preferred = options.find((option) =>
          normalize(this.optionData(option, changedIndex)) === requested
        );
        if (preferred) return preferred;
      }

      return options[0];
    }

    applyVariantToButtons(card, option) {
      if (!option) return;
      card.querySelectorAll('[data-option-group]').forEach((group) => {
        const index = Number(group.dataset.index || 0);
        const value = this.optionData(option, index);
        group.querySelectorAll('[data-option-index]').forEach((button) => {
          const active = normalize(button.dataset.value) === normalize(value);
          button.classList.toggle(ACTIVE, active);
          button.setAttribute('aria-pressed', String(active));
        });
      });
    }

    colorIndex(card) {
      const groups = [...card.querySelectorAll('[data-option-group]')];
      const colorGroup = groups.find((group) => {
        const label = group.querySelector('.embundle-label')?.textContent || '';
        return normalize(label).includes('color');
      });
      return colorGroup ? Number(colorGroup.dataset.index || 0) : 0;
    }

    mediaFor(card, view, option) {
      const choices = this.currentChoices(card);
      const color = choices[this.colorIndex(card)] || '';
      const colorToken = normalize(color);
      const viewToken = normalize(view);
      const media = [...card.querySelectorAll('[data-media]')];

      // Strict color + view match first. Product media is scoped to this card,
      // so an Archive 006 image can never leak into Archive 002/004.
      let match = media.find((node) => {
        const alt = normalize(node.dataset.alt);
        return (!colorToken || alt.includes(colorToken)) && alt.includes(viewToken);
      });

      // Some Shopify alts use only the color for front images.
      if (!match && viewToken === 'front') {
        match = media.find((node) => {
          const alt = normalize(node.dataset.alt);
          return !colorToken || alt.includes(colorToken);
        });
      }

      if (match?.dataset.src) return match.dataset.src;
      if (option?.dataset.image) return option.dataset.image;
      return card.querySelector('[data-main-image]')?.getAttribute('src') || '';
    }

    setViewButtons(card, view) {
      card.querySelectorAll('[data-view]').forEach((button) => {
        const active = button.dataset.view === view;
        button.classList.toggle(ACTIVE, active);
        button.setAttribute('aria-pressed', String(active));
      });
    }

    setView(card, view) {
      card.dataset.currentView = view;
      this.setViewButtons(card, view);
      this.updateCard(card);
    }

    updateUnavailable(card) {
      const choices = this.currentChoices(card);
      const options = this.optionElements(card);

      card.querySelectorAll('[data-option-index]').forEach((button) => {
        const index = Number(button.dataset.optionIndex || 0);
        const candidate = button.dataset.value || '';
        const available = options.some((option) => {
          if (normalize(this.optionData(option, index)) !== normalize(candidate)) return false;
          return this.variantMatches(option, choices, index);
        });

        button.classList.toggle(UNAVAILABLE, !available);
        button.disabled = !available;
        button.setAttribute('aria-disabled', String(!available));
      });
    }

    updateCard(card, changedIndex = -1) {
      const choices = this.currentChoices(card);
      const option = this.matchVariant(card, choices, changedIndex);
      if (!option) return null;

      // If the chosen combination does not exist, visibly reconcile the controls
      // to the exact variant that will be added. No hidden variant drift.
      this.applyVariantToButtons(card, option);

      const select = card.querySelector('[data-select]');
      if (select) select.value = option.value;

      const price = card.querySelector('[data-price-text]');
      if (price) price.textContent = money(option.dataset.price);

      const image = card.querySelector('[data-main-image]');
      if (image) {
        const src = this.mediaFor(card, card.dataset.currentView || 'front', option);
        if (src && image.getAttribute('src') !== src) image.setAttribute('src', src);
      }

      this.updateUnavailable(card);
      return option;
    }

    itemFor(card, option, overrides = {}) {
      const choices = [];
      card.querySelectorAll('[data-option-group]').forEach((group) => {
        const index = Number(group.dataset.index || 0);
        choices[index] = this.optionData(option, index);
      });

      const labels = [...card.querySelectorAll('[data-option-group]')].map((group) => ({
        index: Number(group.dataset.index || 0),
        name: normalize(group.querySelector('.embundle-label')?.textContent || ''),
      }));
      const valueFor = (token) => {
        const found = labels.find((entry) => entry.name.includes(token));
        return found ? choices[found.index] || '' : '';
      };

      return {
        clientKey: overrides.clientKey || (window.crypto?.randomUUID?.() || `slot-${Date.now()}-${Math.random().toString(16).slice(2)}`),
        variantId: String(option.value),
        productId: String(card.dataset.productId || ''),
        productHandle: card.dataset.productHandle || '',
        title: card.dataset.productTitle || 'Archive Piece',
        variantTitle: option.dataset.title || '',
        selectedOptions: choices,
        color: valueFor('color'),
        size: valueFor('size'),
        fabric: valueFor('fabric'),
        view: card.dataset.currentView || 'front',
        price: Number(option.dataset.price || 0),
        image: this.mediaFor(card, card.dataset.currentView || 'front', option),
        url: card.dataset.productUrl || '/cart',
        ...overrides,
      };
    }

    addSlot(item) {
      if (this.selected.length >= 3) {
        this.toast('Bundle holds 3 pieces. Remove one to change it.');
        return;
      }
      this.selected.push(item);
      this.persistDraft();
      this.render();
      this.toast('Added to bundle');
    }

    persistDraft() {
      if (this.bundleState) {
        this.bundleState.setDraft(this.selected);
        return;
      }
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.selected));
      } catch (_) {}
    }

    loadDraft() {
      if (this.bundleState) {
        const draft = this.bundleState.loadDraft();
        return Array.isArray(draft) ? draft : [];
      }
      try {
        const draft = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
        return Array.isArray(draft) ? draft : [];
      } catch (_) {
        return [];
      }
    }

    textAll(selector, value) {
      this.root.querySelectorAll(selector).forEach((node) => { node.textContent = value; });
    }

    toast(message) {
      const node = this.root.querySelector('[data-toast]');
      if (!node) return;
      node.textContent = message;
      node.classList.add(ACTIVE);
      window.setTimeout(() => node.classList.remove(ACTIVE), 1500);
    }

    renderSlots() {
      this.slots.forEach((slot, index) => {
        const item = this.selected[index];
        slot.replaceChildren();
        slot.classList.toggle(FILLED, Boolean(item));

        if (!item) {
          const empty = document.createElement('div');
          empty.className = 'embundle-slot-empty';
          empty.textContent = `Slot ${index + 1} Empty`;
          slot.append(empty);
          return;
        }

        if (item.image) {
          const image = document.createElement('img');
          image.className = 'embundle-slot-img';
          image.src = item.image;
          image.alt = `Selected ${item.title}`;
          slot.append(image);
        }

        const info = document.createElement('div');
        info.className = 'embundle-slot-info';
        const title = document.createElement('strong');
        title.textContent = item.title;
        const variant = document.createElement('span');
        variant.textContent = item.variantTitle || item.selectedOptions.filter(Boolean).join(' / ');
        const price = document.createElement('b');
        price.textContent = money(item.price);
        info.append(title, variant, price);

        const remove = document.createElement('button');
        remove.className = 'embundle-remove';
        remove.type = 'button';
        remove.dataset.removeSlot = String(index);
        remove.setAttribute('aria-label', `Remove ${item.title} from bundle`);
        remove.textContent = '×';

        slot.append(info, remove);
      });
    }

    renderCards() {
      const selectedIds = new Set(this.selected.map((item) => String(item.variantId)));
      this.cards.forEach((card) => {
        const value = card.querySelector('[data-select]')?.value || '';
        card.classList.toggle(CARD_ADDED, selectedIds.has(String(value)));
      });
    }

    render() {
      const count = this.selected.length;
      const eligibleCount = this.otherEligibleQuantity + count;
      const subtotal = this.selected.reduce((sum, item) => sum + Number(item.price || 0), 0);
      const rate = eligibleCount >= 3 ? 0.15 : eligibleCount >= 2 ? 0.10 : 0;
      const total = Math.round(subtotal * (1 - rate));
      const saved = Math.max(0, subtotal - total);
      const width = count >= 3 ? '100%' : count === 2 ? '66%' : count === 1 ? '33%' : '0%';

      const countNode = this.root.querySelector('[data-count]');
      const bar = this.root.querySelector('[data-bar]');
      const progress = this.root.querySelector('[data-progress]');
      const message = this.root.querySelector('[data-message]');
      if (countNode) countNode.textContent = String(count);
      if (bar) bar.style.width = width;
      if (progress) progress.setAttribute('aria-valuenow', String(count));
      if (message) {
        message.textContent = eligibleCount >= 3
          ? `15% tier ready across ${eligibleCount} eligible Archive items; checkout confirms shipping.`
          : eligibleCount === 2
            ? '10% tier ready across the cart and builder.'
            : count === 1
              ? 'Add one more piece to unlock 10% off.'
              : 'Choose your first shirt to begin.';
      }

      this.textAll('[data-live-title]', eligibleCount >= 3 ? '15% Tier Ready' : eligibleCount === 2 ? '10% Tier Ready' : count === 1 ? 'Almost There' : 'Build Your Bundle');
      this.textAll('[data-live-sub]', eligibleCount >= 3 ? `${eligibleCount} eligible Archive items across the cart and builder. Shopify confirms the tier.` : eligibleCount === 2 ? '2 eligible Archive items across the cart and builder. Shopify confirms the tier.' : count === 1 ? 'One more piece unlocks your first reward.' : 'Select 2 shirts to unlock 10% off.');
      this.textAll('[data-live-retail]', `Subtotal ${subtotal ? money(subtotal) : '$0'}`);
      this.textAll('[data-live-total]', subtotal ? money(total) : '$0');
      this.textAll('[data-live-save]', saved ? `Save ${money(saved)}` : 'Add pieces to unlock savings');

      const markOne = this.root.querySelector('[data-mark-one]');
      const markTwo = this.root.querySelector('[data-mark-two]');
      const markThree = this.root.querySelector('[data-mark-three]');
      markOne?.classList.toggle(LIT, count >= 1);
      markTwo?.classList.toggle(LIT, count >= 2);
      markThree?.classList.toggle(LIT, count >= 3);

      this.checkoutButtons.forEach((button) => {
        button.disabled = count < 2 || this.syncing;
        button.textContent = this.syncing ? 'Updating…' : count >= 3 ? 'Add 3-Shirt Bundle' : count === 2 ? 'Add 2-Shirt Bundle' : count === 1 ? 'Add 1 More Piece' : 'Unlock Bundle';
      });

      this.renderSlots();
      this.renderCards();
    }

    cartItemToDraft(line, indexed, copyIndex) {
      const option = indexed.option;
      const card = indexed.card;
      const lineOptions = Array.isArray(line.variant_options) ? line.variant_options : [];

      // Reconcile the visible controls to the cart variant so its color/size/media
      // are represented correctly in both the slot and product card.
      this.applyVariantToButtons(card, option);
      const select = card.querySelector('[data-select]');
      if (select) select.value = option.value;
      this.updateCard(card);

      return this.itemFor(card, option, {
        clientKey: `cart-${line.key}-${copyIndex}`,
        title: line.product_title || card.dataset.productTitle || 'Archive Piece',
        variantTitle: line.variant_title || option.dataset.title || '',
        selectedOptions: lineOptions.length ? [...lineOptions] : undefined,
        price: Number(line.original_price ?? line.price ?? option.dataset.price ?? 0),
        image: line.image || this.mediaFor(card, card.dataset.currentView || 'front', option),
        url: line.url || card.dataset.productUrl || '/cart',
        source: 'cart',
        cartLineKey: line.key,
      });
    }

    async syncFromCart() {
      try {
        const cart = await readCart();
        const found = [];
        let totalEligibleQuantity = 0;

        for (const line of cart.items || []) {
          let indexed = this.variantIndex.get(String(line.variant_id));

          // Product-id fallback handles any edge case where a currently available
          // cart variant was not present in the server-rendered select for some reason.
          if (!indexed && line.product_id) {
            const card = this.productIndex.get(String(line.product_id));
            if (card) {
              const option = this.optionElements(card).find((entry) => String(entry.value) === String(line.variant_id));
              if (option) indexed = { card, option };
            }
          }

          if (!indexed) continue;
          const quantity = Math.max(0, Number(line.quantity || 0));
          totalEligibleQuantity += quantity;

          for (let copy = 0; copy < quantity && found.length < 3; copy += 1) {
            found.push(this.cartItemToDraft(line, indexed, copy));
          }
        }

        this.otherEligibleQuantity = Math.max(0, totalEligibleQuantity - found.length);

        if (found.length) {
          this.selected = found.slice(0, 3);
          this.bundleState?.clearStoredDraft?.();
          // Do not persist cart-hydrated state as an independent draft. The Shopify
          // cart itself remains the authority and will rehydrate on every visit.
        } else {
          this.otherEligibleQuantity = 0;
          this.selected = this.loadDraft().slice(0, 3);
        }

        this.render();
      } catch (error) {
        console.warn('Embrynium bundle cart sync failed.', error);
        this.render();
      }
    }

    scheduleCartSync(delay = 80) {
      window.clearTimeout(this.cartSyncTimer);
      this.cartSyncTimer = window.setTimeout(() => this.syncFromCart(), delay);
    }

    async checkout() {
      if (this.selected.length < 2) {
        this.toast('Select at least 2 pieces first.');
        return;
      }

      if (!this.bundleState?.replaceBundleLines) {
        this.toast('Bundle cart service is still loading. Please try again.');
        return;
      }

      this.syncing = true;
      this.render();
      try {
        await this.bundleState.replaceBundleLines(this.selected);
        window.location.href = window.routes?.cart_url || '/cart';
      } catch (error) {
        console.error('Embrynium bundle checkout failed.', error);
        this.syncing = false;
        this.render();
        this.toast('Cart update failed. Please try again.');
      }
    }

    bindEvents() {
      const signal = this.abortController.signal;

      this.root.addEventListener('click', (event) => {
        const viewButton = event.target.closest('[data-view]');
        if (viewButton && this.root.contains(viewButton)) {
          const card = viewButton.closest('[data-card]');
          if (card) this.setView(card, viewButton.dataset.view);
          return;
        }

        const optionButton = event.target.closest('[data-option-index]');
        if (optionButton && this.root.contains(optionButton)) {
          if (optionButton.disabled || optionButton.classList.contains(UNAVAILABLE)) return;
          const card = optionButton.closest('[data-card]');
          if (!card) return;
          const index = Number(optionButton.dataset.optionIndex || 0);
          card.querySelectorAll(`[data-option-index="${index}"]`).forEach((button) => {
            button.classList.toggle(ACTIVE, button === optionButton);
            button.setAttribute('aria-pressed', String(button === optionButton));
          });
          this.updateCard(card, index);
          return;
        }

        const addButton = event.target.closest('[data-add-piece]');
        if (addButton && this.root.contains(addButton)) {
          const card = addButton.closest('[data-card]');
          if (!card) return;
          const option = this.updateCard(card);
          if (option) this.addSlot(this.itemFor(card, option));
          return;
        }

        const removeButton = event.target.closest('[data-remove-slot]');
        if (removeButton && this.root.contains(removeButton)) {
          this.selected.splice(Number(removeButton.dataset.removeSlot), 1);
          this.persistDraft();
          this.render();
          this.toast('Removed from bundle');
          return;
        }

        const checkoutButton = event.target.closest('[data-checkout]');
        if (checkoutButton && this.root.contains(checkoutButton)) {
          this.checkout();
        }
      }, { signal });

      document.addEventListener('embrynium:cart:updated', () => this.scheduleCartSync(60), { signal });
      document.addEventListener('embrynium:bundle:completed', () => this.scheduleCartSync(60), { signal });
      window.addEventListener('pageshow', () => this.scheduleCartSync(60), { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.scheduleCartSync(60);
      }, { signal });

      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => this.scheduleCartSync(60));
      }
    }
  }

  const boot = () => {
    document.querySelectorAll('.embundle').forEach((root) => {
      try {
        new EmbryniumBundleBuilder(root).init();
      } catch (error) {
        root.dataset.embundleReady = 'error';
        console.error('Embrynium bundle builder failed to initialize.', error);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Shopify theme editor can re-render a section without reloading the page.
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target?.matches?.('.embundle') ? event.target : event.target?.querySelector?.('.embundle');
    if (root && root.dataset.embundleReady !== 'true') {
      new EmbryniumBundleBuilder(root).init();
    }
  });
})();
